import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useDeleteBundle, useUpdateBundle } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import { Bundle, UpdateBundlePayload } from '~/services/AccommodationService';

import BundleCategorySelect from '../components/BundleCategorySelect';
import { ALL_BUNDLE_STATUSES, BundleCategory } from '../constants';

interface Props {
  bundle: Bundle;
}

function AccommodationBundleContentTab({ bundle }: Props) {
  const { push } = useAppNavigation();
  const [name, setName] = useState(bundle.name);
  const [merchandisingLabel, setMerchandisingLabel] = useState(bundle.merchandisingLabel ?? '');
  const [status, setStatus] = useState<NonNullable<UpdateBundlePayload['status']>>(
    bundle.status as NonNullable<UpdateBundlePayload['status']>,
  );
  const [category, setCategory] = useState<BundleCategory | null>(bundle.category);
  const [optIn, setOptIn] = useState<boolean>(bundle.optIn ?? false);
  const [appliesToLeadRate, setAppliesToLeadRate] = useState<boolean>(bundle.appliesToLeadRate ?? false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateBundleMutation = useUpdateBundle();
  const deleteBundleMutation = useDeleteBundle();

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await updateBundleMutation.mutateAsync({
          bundleId: bundle.id,
          // Blank label clears it (null): the customer-facing promotion name then falls back to
          // the underlying promo's name.
          payload: {
            name,
            merchandisingLabel: merchandisingLabel.trim() || null,
            status,
            category,
            optIn,
            appliesToLeadRate,
          },
        });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save bundle');
      }
    },
    [name, merchandisingLabel, status, category, optIn, appliesToLeadRate, updateBundleMutation, bundle.id],
  );

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`Soft-delete bundle "${bundle.name}"?`)) return;
    try {
      await deleteBundleMutation.mutateAsync({ bundleId: bundle.id });
      push('/accommodation/properties#bundles');
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete bundle');
    }
  }, [deleteBundleMutation, bundle.id, bundle.name, push]);

  const isSubmitting = updateBundleMutation.isPending;
  const isDeleting = deleteBundleMutation.isPending;

  return (
    <Box>
      <Box component="form" onSubmit={handleSave}>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextField label="Bundle Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />

          <TextField
            label="Merchandising label"
            value={merchandisingLabel}
            onChange={(e) => setMerchandisingLabel(e.target.value)}
            helperText="Shown to customers as the promotion name on offer tiles (e.g. “Dreamworld Bundle”). The bundle name above is internal only and never shown to customers. Leave blank to fall back to the underlying promo's name."
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as NonNullable<UpdateBundlePayload['status']>)}
              >
                {ALL_BUNDLE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <BundleCategorySelect value={category} onChange={setCategory} />
          </Stack>

          <Box>
            <FormControlLabel
              control={<Checkbox checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />}
              label="Opt-in bundle"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When ticked, the bundled experience is offered as a default-off add-on on the customer page (customer must
              tick to add). When unticked, the bundle is mandatory and baked into the rate price.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox checked={appliesToLeadRate} onChange={(e) => setAppliesToLeadRate(e.target.checked)} />
              }
              label="Applies to all rates (except GHA)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When ticked, the bundle can attach to the cheapest (lead) rate on the property. When unticked, the lead
              rate is excluded from bundling so comparison / metasearch surfaces (e.g. Google Hotel Ads) show un-bundled
              prices.
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save Content'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box mt={6} pt={3} borderTop={1} borderColor="divider">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box fontWeight={500}>Danger zone</Box>
            <Box color="text.secondary" fontSize={14}>
              Soft-delete this bundle. Recovery is possible by Engineering only.
            </Box>
          </Box>
          <Button color="error" variant="outlined" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete Bundle'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AccommodationBundleContentTab;
